import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { auth, db, storage } from '../config/firebase';
import Sidebar from '../components/Sidebar/Sidebar';
import { useAdminRole } from '../hooks/useAdminRole';
import './RamadanMenuAdminPage.css';

const RamadanMenuAdminPage = () => {
  const navigate = useNavigate();
  const [, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentMenu, setCurrentMenu] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const { role: userRole } = useAdminRole();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (!currentUser) {
        setLoading(false);
        navigate('/admin');
        return;
      }

      try {
        const userDocRef = doc(db, 'adminUsers', currentUser.uid);
        const userDocSnap = await getDoc(userDocRef);
        const role = userDocSnap.exists() ? (userDocSnap.data().role ?? 1) : 1;

        if (role !== 0) {
          navigate('/admin');
          return;
        }

        await loadCurrentMenu();
      } catch (error) {
        console.error('Erreur lors du chargement du rôle:', error);
        navigate('/admin');
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const loadCurrentMenu = async () => {
    try {
      const docRef = doc(db, 'siteSettings', 'ramadanMenu');
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        const data = snap.data();
        setCurrentMenu(data);
      } else {
        setCurrentMenu(null);
      }
    } catch (err) {
      console.error('Erreur lors du chargement du menu Ramadan:', err);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    setError('');
    setSuccess('');
  };

  const handleUpload = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!selectedFile) {
      setError('Veuillez sélectionner une image de menu');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const now = new Date();
      const toLocalDateString = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const menuDateObj = new Date(now);
      if (now.getHours() >= 17) {
        menuDateObj.setDate(menuDateObj.getDate() + 1);
      }
      const menuDate = toLocalDateString(menuDateObj);

      const extension = selectedFile.name.split('.').pop() || 'jpg';
      const storagePath = `menu/ramadan-menu-${Date.now()}.${extension}`;
      const storageRef = ref(storage, storagePath);

      const uploadTask = uploadBytesResumable(storageRef, selectedFile);
      const downloadUrl = await new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
            setUploadProgress(progress);
          },
          (error) => reject(error),
          async () => resolve(await getDownloadURL(uploadTask.snapshot.ref))
        );
      });

      if (currentMenu?.imagePath) {
        try {
          await deleteObject(ref(storage, currentMenu.imagePath));
        } catch (deleteError) {
          console.warn('Impossible de supprimer l’ancienne image:', deleteError);
        }
      }

      await setDoc(
        doc(db, 'siteSettings', 'ramadanMenu'),
        {
          imageUrl: downloadUrl,
          imagePath: storagePath,
          menuDate,
          updatedAt: Timestamp.now(),
          updatedBy: auth.currentUser?.uid || null
        },
        { merge: true }
      );

      setSelectedFile(null);
      setSuccess(`Menu Ramadan mis à jour pour le ${menuDate}`);
      await loadCurrentMenu();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      console.error('Erreur lors de l’upload:', err);
      setError('Erreur lors de l’upload: ' + (err.message || 'Erreur inconnue'));
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleToggleEnabled = async (enabled) => {
    setError('');
    setSuccess('');
    try {
      await setDoc(
        doc(db, 'siteSettings', 'ramadanMenu'),
        { enabled, updatedAt: Timestamp.now() },
        { merge: true }
      );
      setCurrentMenu((prev) => ({ ...prev, enabled }));
      setSuccess(enabled ? 'Menu Ramadan activé' : 'Menu Ramadan désactivé');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Erreur: ' + (err.message || 'Erreur inconnue'));
    }
  };

  if (loading) {
    return (
      <div className="ramadan-menu-admin-page">
        <div className="loading-container">
          <p>Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ramadan-menu-admin-page">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} userRole={userRole} />

      <div className="ramadan-menu-admin-content">
        <header className="ramadan-menu-admin-header">
          <button
            className="sidebar-toggle-btn"
            onClick={() => setSidebarOpen(true)}
            aria-label="Toggle sidebar"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
          <h1>Menu Ramadan</h1>
        </header>

        <main className="ramadan-menu-admin-main">
          <div className="ramadan-menu-card">
            <h2>Affichage du menu Ramadan</h2>
            <div className="toggle-row">
              <span className="toggle-label">
                Afficher le menu Ramadan aux utilisateurs
              </span>
              <button
                type="button"
                role="switch"
                aria-checked={currentMenu?.enabled !== false}
                className={`toggle-switch ${currentMenu?.enabled !== false ? 'on' : 'off'}`}
                onClick={() => handleToggleEnabled(currentMenu?.enabled === false)}
              >
                <span className="toggle-slider" />
              </button>
            </div>
            <p className="toggle-hint">
              {currentMenu?.enabled !== false
                ? 'Les utilisateurs voient le menu Ramadan par défaut.'
                : 'Les utilisateurs voient le menu habituel (pizza, plats).'}
            </p>
          </div>

          <div className="ramadan-menu-card">
            <h2>Mettre à jour le menu Ramadan</h2>

            {error && (
              <div className="message error-message">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                {error}
              </div>
            )}

            {success && (
              <div className="message success-message">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                {success}
              </div>
            )}

            <form onSubmit={handleUpload}>
              <div className="form-group">
                <label htmlFor="menuImage">Image du menu *</label>
                <input
                  type="file"
                  id="menuImage"
                  accept="image/*"
                  onChange={handleFileChange}
                  disabled={uploading}
                />
                {selectedFile && (
                  <div className="file-info">
                    Fichier sélectionné: <strong>{selectedFile.name}</strong>
                  </div>
                )}
              </div>

              {uploading && (
                <div className="upload-progress">
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: `${uploadProgress}%` }} />
                  </div>
                  <p className="progress-text">{uploadProgress}%</p>
                </div>
              )}

              <button type="submit" className="btn-primary" disabled={uploading}>
                {uploading ? 'Upload en cours...' : 'Mettre à jour le menu'}
              </button>
            </form>
          </div>

          <div className="ramadan-menu-card">
            <h2>Menu Ramadan actuel</h2>
            {currentMenu?.imageUrl ? (
              <>
                <div className="menu-info">
                  <p>
                    <strong>Menu du:</strong> {currentMenu.menuDate || 'N/A'}
                  </p>
                </div>
                <img
                  src={currentMenu.imageUrl}
                  alt="Menu Ramadan"
                  className="ramadan-menu-preview"
                />
              </>
            ) : (
              <p className="empty-state">Aucun menu Ramadan défini.</p>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default RamadanMenuAdminPage;
